
import { useEffect, useMemo, useState } from 'react';

import groupBy from 'lodash/groupBy';
import Tabs from 'antd/es/tabs';
import { Switch } from 'antd';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt';

import { MOBILE_MEDIA_POINT, Routes } from '@share/constants';
import { getQuotes, markQuotesAsRead } from '@share/store/slices';
import { RootState, getKeyQuotesFromPath, LoginType, isQuotesFromPath } from '@share/utils';
import { SearchTable } from '@components';
import { IColumnMenu, NULL_VALUE } from '@constants';
import { QuotePackageModalCompare } from '../common/quote-package-modal-compare';
import { CARS_KEY, GetMyQuotesTabs, GetQuoteDatasourceData, HOTELS_KEY } from './utils';

import './style.scss';

// Default ordering for every tab: newest first by raw creation date (ISO strings sort
// chronologically, unlike the formatted date shown in the cell).
const sortGroupsByCreationDateDesc = (grouped: Record<string, any[]>): [string, any[]][] =>
  Object.entries(grouped).sort(([, a], [, b]) => (b[0]?.creationDate || '').localeCompare(a[0]?.creationDate || ''));

export function MyQuotes() {
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const navigationMenuStore = useSelector((state: RootState) => state.navigationMenuStore);
  
  const { items } = navigationMenuStore;
  const { account, user } = loginStore;
  const { loading, result } = quoteStore;

  const { quotesHotels, quotesCondos, quotesCars, quotesPackages, quotesActivities } = result;

  const [visibleModal, setVisibleModal] = useState(false);
  const [quoteBase, setQuoteBase] = useState<string | null>(null);
  // Hides expired ("historical") quotes from the current tab. Off by default and reset to
  // off whenever the user switches tabs.
  const [hideExpired, setHideExpired] = useState(false);

  const isMobile = document.body.offsetWidth <= MOBILE_MEDIA_POINT;
  
  const dispatch = useDispatch();

  const history = useHistory();

  if (!account || account.type === LoginType.Public) {
    history.push(Routes.NotAuthorized);
  }

  useEffect(() => {
    if (!loginStore.loading) {
      const param = getKeyQuotesFromPath();
      setQuoteBase(param);
      dispatch(getQuotes(param));

      const isQuotes = isQuotesFromPath(history);
      if (isQuotes && param) {
        dispatch(markQuotesAsRead(param));
      }
    }
  }, [loginStore.loading])

  useEffect(() => {
    if (!!quoteStore.result.selectedQuotes?.length) {
      setVisibleModal(true);
    }
  }, [quoteStore.result.selectedQuotes])

  const handleOnRefresh = () => {
    if (user) {
      dispatch(getQuotes());
    }
  };

  const dataSourceHotels = useMemo(() => {
    if (!quotesHotels.length) return [];
    const grouped = groupBy(quotesHotels, quote => quote.quoteBaseId);
    return sortGroupsByCreationDateDesc(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quote));
  }, [quotesHotels, loginStore]);

  const dataSourceCondos = useMemo(() => {
    if (!quotesCondos.length) return [];
    const grouped = groupBy(quotesCondos, quote => quote.quoteBaseId);
    return sortGroupsByCreationDateDesc(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quote));
  }, [quotesCondos, loginStore]);

  const dataSourceCars = useMemo(() => {
    if (!quotesCars.length) return [];
    const grouped = groupBy(quotesCars, quote => quote.quoteBaseId);
    return sortGroupsByCreationDateDesc(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quote));
  }, [quotesCars, loginStore]);

  const dataSourceActivities = useMemo(() => {
    if (!quotesActivities.length) return [];
    const grouped = groupBy(quotesActivities, quote => quote.quoteBaseId);
    return Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesActivities));
  }, [quotesActivities]);

  const dataSourcePackages = useMemo(() => {
    if (!quotesPackages.length) return [];
    const grouped = groupBy(quotesPackages, quote => quote.quoteBaseId);
    return sortGroupsByCreationDateDesc(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quote));
  }, [quotesPackages, loginStore]);

  const isCarsShareCompareEnabled = useMemo(() => account?.carsShareCompareEnabled && 
                                        (!account?.isRSITemplate || ((!account?.carsShareCompareRsiSiteidList?.length && !account?.carsShareCompareRsiOrgidList?.length) || 
                                        (account?.carsShareCompareRsiSiteidList?.includes(items?.siteId?.toString() || '') || account?.carsShareCompareRsiOrgidList?.includes(items?.organizationId?.toString() || '')))), [account, items]);


  const tabs = useMemo(() => {
    const allTabs = GetMyQuotesTabs(isMobile, dataSourceHotels, dataSourceCondos, dataSourceCars, dataSourceActivities, dataSourcePackages, setVisibleModal, setQuoteBase, (quoteId) => {
      dispatch(markQuotesAsRead(quoteId));
    });
    if (!isCarsShareCompareEnabled) {
      return allTabs.filter(tab => tab.key !== CARS_KEY);
    }
    return allTabs;
  }, [isMobile, loginStore, dataSourceHotels, dataSourceCondos, dataSourceCars, dataSourceActivities, dataSourcePackages, isCarsShareCompareEnabled, dispatch]);

  const buttonTextColor = account?.buttonTextColor;
  const buttonBorder = account?.buttonBorder;
  const buttonBorderRadius = account?.buttonBorderRadius;
  const buttonColor = account?.buttonColor;

  const styleButtonBorderRadius = !isEmpty(buttonBorderRadius) ? { borderRadius: `${buttonBorderRadius}px` } : {};  
  const styleButtonBorder = !isEmpty(buttonBorder)? { ...styleButtonBorderRadius, border: buttonBorder } : { ...styleButtonBorderRadius };  
  const styleButtonColor = !isEmpty(buttonTextColor)? { ...styleButtonBorder, color: buttonTextColor } : { ...styleButtonBorder };  
  const styleButton = !isEmpty(buttonColor)? { ...styleButtonColor, background: buttonColor } : { ...styleButtonColor };  

  return (
    <div className={`quotes-wrapper ${isMobile ? 'mobile' : ''}`}>
      <div className="quotes-wrapper__controls">
        <div className="quotes-wrapper__hide-expired">
          <Switch size="small" checked={hideExpired} onChange={setHideExpired} />
          <span><FormattedMessage id="quotes.hide.expired" defaultMessage="Hide expired" /></span>
        </div>
        <div className="quotes-wrapper__refresh" onClick={handleOnRefresh}>
          <div className="action-button" style={styleButton}>
            <FontAwesomeIcon icon={faSyncAlt} size="1x" />
            <span>Refresh</span>
          </div>
        </div>
      </div>

      <Tabs defaultActiveKey={HOTELS_KEY} onChange={() => setHideExpired(false)}>
        {tabs.map(({ title, columns, datasource, key }) => {
          const visibleData = hideExpired ? datasource.filter((row: any) => !row.isExpired) : datasource;
          return (
          <Tabs.TabPane key={key} tab={<FormattedMessage id={title} />}>
            <SearchTable
              title={NULL_VALUE}
              loading={loading}
              actions={NULL_VALUE}
              allColumns={columns as IColumnMenu[]}
              dataSource={visibleData}
              pagination={{
                showSizeChanger: false,
                showQuickJumper: false
              }}
              filters={NULL_VALUE}
              totalOrderCount={visibleData.length}
              rowKey={(row: any) => row.key ? `${row.key}` : `${row.quoteBase}`}
              hideRefresh={true}
              isFilterApplied={false}
              onFiltersChange={NULL_VALUE}
              onTableChange={NULL_VALUE}
              onResetFilters={NULL_VALUE}
              onApplyFilters={NULL_VALUE}
              onRefresh={() => {}}
            ></SearchTable>
          </Tabs.TabPane>
          );
        })}
      </Tabs>

      {visibleModal && quoteBase ?
        <QuotePackageModalCompare
          visible={visibleModal}
          quoteBase={quoteBase}
          onCancel={() => { setVisibleModal(false); setQuoteBase(null) }}
        /> : null}
    </div>

  );
}
